"use client";

import { withLivingMapLang } from "@/lib/livingMap";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import {
  Home,
  ChevronRight,
  Gauge,
  CreditCard,
  Banknote,
  Phone,
  Mail,
  ExternalLink,
  ArrowUpRight,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import OtrasOpciones from "../components/OtrasOpciones";
import ExternalLinkModal from "@/components/ExternalLinkModal/ExternalLinkModal";
import MapButton from "../../../components/MapButton";

const NAVY = "#102542";
const YELLOW = "#ffb800";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Metro%20de%20Panam%C3%A1/9M1A2785.jpg";

const MAPA_METRO_URL =
  "https://galeria.tocumenaero.com/gallery/transporte/metropanama.png";

const MAPA_METRO =
  "https://galeria.tocumenaero.com/gallery/api/Metro%20de%20Panam%C3%A1/321A9929.jpg";

const SHUTTLE_T1_MAP =
  "https://airportpanama.lmp.prod.livingmap.com/poi?feature=9ea21059865cec9b9f187d31535ebdd2&floor=1&lang=en-GB&name-search=enable&query=Travel%20Data%20Global#18.13/9.066832/-79.38747/119";
const SHUTTLE_T2_MAP =
  "https://airportpanama.lmp.prod.livingmap.com/poi?feature=6ee3c5ac3b566ef6ad673577fb0546d4&floor=1&lang=en-GB&name-search=enable&query=Travel%20Data%20Global#18.13/9.065763/-79.389685/119";

const SHUTTLE_IMG_T1 =
  "https://galeria.tocumenaero.com/gallery/api/Shuttle bus externo/321A8612.jpg";
const SHUTTLE_IMG_T2 =
  "https://galeria.tocumenaero.com/gallery/api/Shuttle%20bus%20externo/321A8601.jpg";

type TerminalCardProps = {
  image: string;
  label: string;
  cta: string;
  mapSrc: string;
  gate: string;
};

function TerminalCard({ image, label, cta, mapSrc, gate }: TerminalCardProps) {
  return (
    <article className="group relative bg-white grid grid-cols-2 items-stretch transition-colors hover:bg-gray-50 overflow-hidden min-h-[160px]">
      <span
        className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 z-10"
        style={{ backgroundColor: YELLOW }}
      />
      <div className="relative w-full h-full overflow-hidden bg-gray-100">
        <Image
          src={image}
          alt={label}
          fill
          sizes="(max-width: 768px) 50vw, 25vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6 flex flex-col justify-center min-w-0">
        <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">
          {gate}
        </span>
        <h4
          className="text-base font-bold tracking-tight mb-3"
          style={{ color: NAVY }}
        >
          {label}
        </h4>
        <MapButton src={mapSrc} variant="link" label={cta} />
      </div>
    </article>
  );
}

type Destino = {
  nombre: string;
  tiempo: string;
  href: string;
  imagen: string;
};

export default function MetroPage() {
  const t = useTranslations("metroPage");
  const tCrumb = useTranslations("breadcrumb");
  const tTransporte = useTranslations("nav.transporte");
  const locale = useLocale();

  const destinos = t.raw("destinos.items") as Destino[];

  const [modalUrl, setModalUrl] = useState<string | null>(null);

  const openExternal = (url: string) => setModalUrl(url);
  const closeModal = () => setModalUrl(null);
  const handleContinue = () => {
    // Estas URLs se abren directo (no pasan por /map), así que el idioma
    // hay que ajustarlo acá.
    if (modalUrl) window.open(withLivingMapLang(modalUrl, locale), "_blank");
    closeModal();
  };

  const ventajas = [
    {
      key: "rapido",
      icon: Gauge,
      title: t("ventajas.rapido.title"),
      desc: t("ventajas.rapido.desc"),
    },
    {
      key: "pago",
      icon: CreditCard,
      title: t("ventajas.pago.title"),
      desc: t("ventajas.pago.desc"),
    },
    {
      key: "cajeros",
      icon: Banknote,
      title: t("ventajas.cajeros.title"),
      desc: t("ventajas.cajeros.desc"),
    },
  ];

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("hero.title")}
          fill
          priority
          sizes="100vw"
          className="object-cover blur-xs"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>{tCrumb("home")}</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li>
                <Link
                  href={`/${locale}/transporte`}
                  className="hover:text-white transition-colors"
                >
                  {tTransporte("label")}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li
                aria-current="page"
                className="text-white font-medium truncate"
              >
                {t("breadcrumb")}
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px" style={{ backgroundColor: YELLOW }} />
              {t("hero.eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("hero.title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Explora las líneas del Metro */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 max-w-2xl">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("lineas.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                {t("lineas.title")}
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                {t("lineas.subtitle")}
              </p>

              <button
                type="button"
                onClick={() => openExternal(MAPA_METRO_URL)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors hover:opacity-90"
                style={{ backgroundColor: YELLOW, color: NAVY }}
              >
                {t("lineas.cta")}
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="lg:col-span-5 lg:col-start-8 relative aspect-[4/3] rounded-2xl overflow-hidden border border-gray-200">
              <Image
                src={MAPA_METRO}
                alt={t("lineas.title")}
                fill
                sizes="(max-width: 1024px) 100vw, 40vw"
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Shuttle externo → estación del Metro */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("shuttleExterno.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("shuttleExterno.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("shuttleExterno.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            <TerminalCard
              image={SHUTTLE_IMG_T1}
              label={t("shuttleExterno.t1Label")}
              cta={t("shuttleExterno.verEnMapa")}
              mapSrc={SHUTTLE_T1_MAP}
              gate={t("shuttleExterno.t1gate")}
            />
            <TerminalCard
              image={SHUTTLE_IMG_T2}
              label={t("shuttleExterno.t2Label")}
              cta={t("shuttleExterno.verEnMapa")}
              mapSrc={SHUTTLE_T2_MAP}
              gate={t("shuttleExterno.t2gate")}
            />
          </div>
        </div>
      </section>

      {/* Centros comerciales conectados */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("destinos.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("destinos.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("destinos.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {destinos.map((d, idx) => (
              <button
                key={d.nombre}
                type="button"
                onClick={() => openExternal(d.href)}
                className="group relative bg-white text-left flex flex-col transition-colors hover:bg-gray-50 overflow-hidden"
              >
                <span
                  className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 z-10"
                  style={{ backgroundColor: YELLOW }}
                />
                <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
                  <Image
                    src={d.imagen}
                    alt={d.nombre}
                    fill
                    sizes="(max-width: 768px) 100vw, 25vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex flex-col gap-2">
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-gray-400 transition-all duration-300 group-hover:text-[#102542] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>
                  <h3
                    className="text-base font-bold tracking-tight"
                    style={{ color: NAVY }}
                  >
                    {d.nombre}
                  </h3>
                  <p className="text-xs text-gray-500 font-mono uppercase tracking-wider tabular-nums">
                    {d.tiempo}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Viaja sin complicaciones — ventajas */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("viajeSinComplicaciones.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("viajeSinComplicaciones.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("viajeSinComplicaciones.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {ventajas.map((v, idx) => {
              const Icon = v.icon;
              return (
                <article
                  key={v.key}
                  className="bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between mb-6">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "#f5f5f5", color: NAVY }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <h3
                    className="text-lg font-bold tracking-tight mb-3"
                    style={{ color: NAVY }}
                  >
                    {v.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {v.desc}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section className="bg-[#102542]">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
                <span
                  className="w-8 h-px"
                  style={{ backgroundColor: YELLOW }}
                />
                {t("contacto.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                {t("contacto.title")}
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed">
                {t("contacto.subtitle")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              <a
                href="tel:+50767812906"
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <Phone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("contacto.callLabel")}
                  </p>
                  <p className="text-white font-semibold tabular-nums">
                    (+507) 6781-2906
                  </p>
                </div>
              </a>

              <a
                href="https://wa.me/50767812906"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <FaWhatsapp className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("contacto.whatsappLabel")}
                  </p>
                  <p className="text-white font-semibold tabular-nums">
                    (+507) 6781-2906
                  </p>
                </div>
              </a>

              <a
                href="mailto:atencionmetro@metrodepanama.com.pa"
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("contacto.emailLabel")}
                  </p>
                  <p className="text-white font-semibold truncate">
                    atencionmetro@metrodepanama.com.pa
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Otras opciones */}
      <div className="max-w-[80%] mx-auto">
        <OtrasOpciones actual="metro" />
      </div>

      <ExternalLinkModal
        isOpen={!!modalUrl}
        onClose={closeModal}
        onContinue={handleContinue}
      />
    </main>
  );
}
